import type { PodiumEntry } from "@/api/types";
import { PodiumCard } from "./PodiumCard";
import { Confetti } from "@/components/effects/Confetti";

export interface PodiumProps {
  entries: PodiumEntry[];
  metricLabel: string;
}

/** Placeholder shown when we don't have 3 competitors yet. Keeps the
 * 2-1-3 podium silhouette intact so the layout doesn't collapse. */
function PlaceholderCard({ rank }: { rank: 1 | 2 | 3 }) {
  return (
    <div
      className="flex flex-col items-center justify-center gap-2 rounded-[20px] border border-dashed border-border-2 bg-white/[0.015] p-6 text-center"
      style={{ minHeight: rank === 1 ? 290 : 260 }}
      aria-hidden
    >
      <div className="font-display text-2xl text-text-4">{rank}º</div>
      <div className="text-xs text-text-4">Esperando competidores</div>
    </div>
  );
}

/** Renders up to 3 cards in the visual order 2-1-3 so the 1st place sits
 * in the middle with the tallest elevation — the classic Olympic podium
 * arrangement. Missing positions render placeholders so the silhouette
 * stays intact. Fires a one-off confetti burst when there's a champion. */
export function Podium({ entries, metricLabel }: PodiumProps) {
  if (entries.length === 0) return null;

  const first = entries[0];
  const second = entries[1];
  const third = entries[2];

  return (
    <div className="relative grid grid-cols-3 items-end gap-4 pt-8">
      {first && <Confetti />}
      <div>
        {second ? (
          <PodiumCard entry={second} rank={2} metricLabel={metricLabel} />
        ) : (
          <PlaceholderCard rank={2} />
        )}
      </div>
      <div>
        {first ? (
          <PodiumCard entry={first} rank={1} metricLabel={metricLabel} />
        ) : (
          <PlaceholderCard rank={1} />
        )}
      </div>
      <div>
        {third ? (
          <PodiumCard entry={third} rank={3} metricLabel={metricLabel} />
        ) : (
          <PlaceholderCard rank={3} />
        )}
      </div>
    </div>
  );
}
